import * as babylon from "@babylonjs/core";

import { BabylonState } from "../../state";

/**
 * Syncs the data-space position of all entities with the current time.
 *
 * TODO(dave): this is currently logarithmic complexity for all entities.
 * Decouple it from the render loop.
 * Using low/high LODs will also set a cap on the number of points that
 * need to be inspected.
 */
export function updatePositions(state: BabylonState) {
  const time = state.sceneConfig.time;
  if (time === undefined) {
    return;
  }
  for (const entity of Object.values(state.entities)) {
    if (!entity.position || entity.position.cache.load.state !== "loaded") {
      continue;
    }
    const cache = entity.position.cache;
    // Figure out what index from the position cache to load.
    // This could possibly be implemented with an amortized linear search instead
    // by using the currently-loaded logtime, but that only
    // works at slow playback speeds, and doesn't work for fast
    // playback or seeking.
    // The logtime binary search will also be reusable for buffering data
    // when we decide to downsample the full path followed by the Entity
    // eg we can downsample to 1hz or so for the full path, and then buffer playback
    // for a few seconds based on the render framerate (60fps / playbackSpeed)
    const index = logTimeBinarySearch(cache.logtimes, time);
    const lastTime = cache.logtimes.at(index);
    const lastPosition = cache.allPoints.at(index);
    if (lastTime === undefined || lastPosition === undefined) {
      continue;
    }
    const nextTime = cache.logtimes.at(index + 1);
    const nextPosition = cache.allPoints.at(index + 1);
    // Apply the position/time pair.
    if (nextTime === undefined || nextPosition === undefined) {
      entity.position.current.logtime = lastTime;
      entity.position.current.vector = lastPosition;
    } else {
      const lerpAmount = Math.min(
        Math.max(Number(time - lastTime) / Number(nextTime - lastTime), 0),
        1,
      );

      const lerpPosition = babylon.Vector3.Lerp(
        lastPosition,
        nextPosition,
        lerpAmount,
      );
      entity.position.current.logtime = time;
      entity.position.current.vector = lerpPosition;
    }
  }
}

/**
 * Finds the closest index of the given log time array at or before the target logtime.
 *
 * If target comes before the first logtime, returns index 0.
 * If the target comes after the last logtime, returns the last index.
 */
export function logTimeBinarySearch(arr: bigint[], target: bigint): number {
  let left = 0;
  let right = arr.length - 1;

  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    const midValue = arr[mid];

    if (midValue === target) {
      return mid; // Target found
    } else if (midValue < target) {
      left = mid + 1;
    } else {
      right = mid - 1;
    }
  }

  return Math.max(0, left - 1); // Target not found exactly, return the index before the target.
}
